import "react-datepicker/dist/react-datepicker.css"

import { ApplicationStateEnumOptions } from "@boundless/bl-api-client/customer/models/ApplicationStateEnumLabels"
import { ApplicationStatusEnumOptions } from "@boundless/bl-api-client/customer/models/ApplicationStatusEnumLabels"
import { CustomerApplicationsUpdateBody } from "@boundless/bl-api-client/customer/services.zod"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Table as TanstackTable,
} from "@tanstack/react-table"
import DatePicker from "react-datepicker"

import { Button } from "@/components/ui/button"
import { Card, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { getApplicationsColumns } from "@/lib/data-tables/application-columns"

import type { CustomerApplications } from "@boundless/bl-api-client/customer/models/CustomerApplications"
import type { z } from "zod"

type ApplicationsTableProps = {
  applications: CustomerApplications[]
  onToggle: (
    data: z.infer<typeof CustomerApplicationsUpdateBody>,
    uuid: string,
  ) => void
}

const MOBILE_LABELS: Partial<Record<string, string>> = {
  lenderName: "Lender",
  lastModifiedAt: "Last Updated",
  submittedAt: "Submitted",
  state: "State",
  status: "Status",
  repositoryAccessEnabled: "Data Room",
  details: "Details",
  downloadUrl: "Download",
}

function getColumnLabel(id: string): string {
  const label = MOBILE_LABELS[id]
  if (label) return label

  const spaced = id.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function MobileApplicationsList({
  table,
}: {
  table: TanstackTable<CustomerApplications>
}) {
  const rows = table.getRowModel().rows

  if (!rows.length) {
    return (
      <div className="text-muted-foreground rounded-md border p-6 text-center text-sm">
        No results.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <Card key={row.id} className="p-4">
          <div className="space-y-3">
            {row.getVisibleCells().map((cell) => {
              const label = getColumnLabel(cell.column.id)

              return (
                <div
                  key={cell.id}
                  className="flex items-start justify-between gap-4"
                >
                  <div className="text-muted-foreground min-w-0 text-xs font-medium">
                    {label}
                  </div>
                  <div className="min-w-0 text-right text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      ))}
    </div>
  )
}

export function ApplicationsTable({
  applications,
  onToggle,
}: ApplicationsTableProps) {
  const columns = getApplicationsColumns({ onToggle })

  const table = useReactTable({
    data: applications,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [{ id: "submittedAt", desc: true }],
    },
  })

  const lenderOptions = Array.from(
    new Set(applications.map((app) => app.lenderName)),
  )
    .filter((name): name is string => Boolean(name))
    .map((name) => ({ value: name, label: name }))

  const stateOptions = ApplicationStateEnumOptions.filter(({ value }) =>
    applications.some((app) => app.state === value),
  )

  const statusOptions = ApplicationStatusEnumOptions.filter(({ value }) =>
    applications.some((app) => app.status === value),
  )

  const getDateFilter = (
    id: string,
  ): { from: Date | null; to: Date | null } => {
    return (
      (table.getColumn(id)?.getFilterValue() as {
        from: Date | null
        to: Date | null
      }) ?? { from: null, to: null }
    )
  }

  const setDateFilter = (
    id: string,
    updates: Partial<{ from: Date | null; to: Date | null }>,
  ): void => {
    const current = getDateFilter(id)
    table.getColumn(id)?.setFilterValue({
      from: updates.from ?? current.from,
      to: updates.to ?? current.to,
    })
  }

  return (
    <>
      <CardTitle className="mb-4 lg:mb-8">Applications</CardTitle>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-8">
        <div className="space-y-2">
          <Label className="text-base font-medium">Lender</Label>
          <Select
            value={
              (table.getColumn("lenderName")?.getFilterValue() as string) ?? ""
            }
            onValueChange={(value) =>
              table.getColumn("lenderName")?.setFilterValue(value || undefined)
            }
          >
            <SelectTrigger className="h-9 w-full lg:!h-9">
              <SelectValue placeholder="Select lender" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {lenderOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 lg:col-span-2">
          <Label className="text-base font-medium">Last Updated</Label>
          <div className="flex flex-col gap-2 lg:flex-row lg:gap-2 lg:space-x-2">
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="From"
                selected={getDateFilter("lastModifiedAt").from}
                onChange={(date) =>
                  setDateFilter("lastModifiedAt", { from: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="To"
                selected={getDateFilter("lastModifiedAt").to}
                onChange={(date) =>
                  setDateFilter("lastModifiedAt", { to: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2 lg:col-span-2">
          <Label className="text-base font-medium">Submitted</Label>
          <div className="flex flex-col gap-2 lg:flex-row lg:gap-2 lg:space-x-2">
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="From"
                selected={getDateFilter("submittedAt").from}
                onChange={(date) =>
                  setDateFilter("submittedAt", { from: date })
                }
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
            <div className="min-w-0 flex-1">
              <DatePicker
                placeholderText="To"
                selected={getDateFilter("submittedAt").to}
                onChange={(date) => setDateFilter("submittedAt", { to: date })}
                dateFormat="yyyy-MM-dd"
                wrapperClassName="w-full"
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                showYearDropdown
                dropdownMode="select"
                enableTabLoop={false}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">State</Label>
          <Select
            value={(table.getColumn("state")?.getFilterValue() as string) ?? ""}
            onValueChange={(value) =>
              table.getColumn("state")?.setFilterValue(value || undefined)
            }
          >
            <SelectTrigger className="h-9 w-full lg:!h-9">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {stateOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value ?? ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">Status</Label>
          <Select
            value={
              (table.getColumn("status")?.getFilterValue() as string) ?? ""
            }
            onValueChange={(value) =>
              table.getColumn("status")?.setFilterValue(value || undefined)
            }
          >
            <SelectTrigger className="h-9 w-full lg:!h-9">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value ?? ""}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end sm:col-span-2 lg:col-span-1">
          <Button
            variant="outline"
            className="h-9 w-full"
            onClick={() => table.resetColumnFilters()}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <div className="lg:hidden">
        <MobileApplicationsList table={table} />
      </div>

      <div className="hidden lg:block">
        <Table className="w-full table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="w-40 whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
}
